import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  BookOpen, Package, CreditCard, ChevronLeft,
  MapPin, Phone, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BASE_PRICE = 60;
const EXTRA_COPY_PRICE = 20;
const COLLABORATOR_SHIPPING = 5;

const countries = [
  { value: 'ES', label: 'España' },
  { value: 'FR', label: 'Francia' },
  { value: 'DE', label: 'Alemania' },
  { value: 'IT', label: 'Italia' },
  { value: 'PT', label: 'Portugal' },
  { value: 'GB', label: 'Reino Unido' },
  { value: 'NL', label: 'Países Bajos' },
  { value: 'BE', label: 'Bélgica' },
];

const OrderPage = () => {
  const { t } = useTranslation();
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [shippingData, setShippingData] = useState({
    shipping_name: '',
    shipping_address: '',
    shipping_city: '',
    shipping_postal_code: '',
    shipping_country: 'ES',
    shipping_phone: '',
  });

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/books/${bookId}`, { withCredentials: true });
        if (response.data.status !== 'ready') {
          toast.error('El libro aún no está listo para pedir');
          navigate(`/books/${bookId}`);
          return;
        }
        setBook(response.data);
      } catch (error) {
        console.error('Error fetching book:', error);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [bookId, navigate]);

  const total = BASE_PRICE + (quantity > 1 ? (quantity - 1) * EXTRA_COPY_PRICE : 0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const required = ['shipping_name', 'shipping_address', 'shipping_city', 'shipping_postal_code', 'shipping_phone'];
    const missing = required.filter(field => !shippingData[field]);
    
    if (missing.length > 0) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setSubmitting(true);
    try {
      // Create order
      const orderResponse = await axios.post(`${API_URL}/api/orders`, {
        book_id: bookId,
        quantity,
        ...shippingData,
      }, { withCredentials: true });

      const orderId = orderResponse.data.order_id;
      toast.success('Pedido creado correctamente');
      navigate(`/orders/${orderId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pedido');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!book) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <Button
            variant="ghost"
            onClick={() => navigate(`/books/${bookId}`)}
            className="mb-6 gap-2"
            data-testid="back-to-book"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('common.back')}
          </Button>

          <h1 className="font-heading text-3xl font-bold text-foreground mb-8">
            {t('order.checkout')}
          </h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Order Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Shipping Info */}
                <Card className="card-elevated" data-testid="shipping-form">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      {t('order.shippingInfo')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="input-label">
                        <User className="w-4 h-4 inline mr-2" />
                        {t('order.fullName')}
                      </Label>
                      <Input
                        value={shippingData.shipping_name}
                        onChange={(e) => setShippingData({ ...shippingData, shipping_name: e.target.value })}
                        className="input-field"
                        required
                        data-testid="shipping-name"
                      />
                    </div>

                    <div>
                      <Label className="input-label">{t('order.address')}</Label>
                      <Input
                        value={shippingData.shipping_address}
                        onChange={(e) => setShippingData({ ...shippingData, shipping_address: e.target.value })}
                        className="input-field"
                        placeholder="Ejem: Calle Principal 123, 2º B"
                        required
                        data-testid="shipping-address"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="input-label">{t('order.city')}</Label>
                        <Input
                          value={shippingData.shipping_city}
                          onChange={(e) => setShippingData({ ...shippingData, shipping_city: e.target.value })}
                          className="input-field"
                          required
                          data-testid="shipping-city"
                        />
                      </div>
                      <div>
                        <Label className="input-label">{t('order.postalCode')}</Label>
                        <Input
                          value={shippingData.shipping_postal_code}
                          onChange={(e) => setShippingData({ ...shippingData, shipping_postal_code: e.target.value })}
                          className="input-field"
                          required
                          data-testid="shipping-postal-code"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="input-label">{t('order.country')}</Label>
                      <Select 
                        value={shippingData.shipping_country} 
                        onValueChange={(v) => setShippingData({ ...shippingData, shipping_country: v })}
                      >
                        <SelectTrigger className="input-field" data-testid="shipping-country">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country.value} value={country.value}>
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="input-label">
                        <Phone className="w-4 h-4 inline mr-2" />
                        {t('order.phone')}
                      </Label>
                      <Input
                        type="tel"
                        value={shippingData.shipping_phone}
                        onChange={(e) => setShippingData({ ...shippingData, shipping_phone: e.target.value })}
                        className="input-field"
                        placeholder="Ejem: +34 612 345 678"
                        required
                        data-testid="shipping-phone"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Quantity Selection */}
                <Card className="card-elevated" data-testid="quantity-card">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Package className="w-5 h-5 text-primary" />
                      {t('order.quantity')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                        data-testid="quantity-decrease"
                      >
                        -
                      </Button>
                      <span className="text-2xl font-semibold w-12 text-center">{quantity}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setQuantity(quantity + 1)}
                        data-testid="quantity-increase"
                      >
                        +
                      </Button>
                      <span className="text-muted-foreground">
                        {quantity === 1 ? t('orderTracking.copy') : t('orderTracking.copies')}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Submit Button (mobile) */}
                <div className="lg:hidden">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary text-lg gap-2"
                    data-testid="pay-now-mobile"
                  >
                    <CreditCard className="w-5 h-5" />
                    {submitting ? t('common.loading') : `${t('order.payNow')} - ${total.toFixed(2)}€`}
                  </Button>
                </div>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="card-elevated sticky top-24" data-testid="order-summary">
                <CardHeader>
                  <CardTitle className="font-heading">{t('order.summary')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Book Info */}
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-20 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium line-clamp-2">{book.title}</h3>
                      <p className="text-sm text-muted-foreground">{t('bookDetail.for')} {book.profile.recipient_name}</p>
                    </div>
                  </div>

                  <Separator />

                  {/* Pricing */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Primer ejemplar</span>
                      <span>{BASE_PRICE.toFixed(2)}€</span>
                    </div>
                    {quantity > 1 && (
                      <div className="flex justify-between text-sm">
                        <span>{quantity - 1} ejemplar{quantity > 2 ? 'es' : ''} extra x {EXTRA_COPY_PRICE}€</span>
                        <span>{((quantity - 1) * EXTRA_COPY_PRICE).toFixed(2)}€</span>
                      </div>
                    )}
                  </div>

                  <Separator />

                  <div className="flex justify-between font-semibold text-lg">
                    <span>{t('order.total')}</span>
                    <span>{total.toFixed(2)}€</span>
                  </div>

                  {/* Submit Button (desktop) */}
                  <div className="hidden lg:block pt-4">
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="w-full btn-primary text-lg gap-2"
                      data-testid="pay-now-desktop"
                    >
                      <CreditCard className="w-5 h-5" />
                      {submitting ? t('common.loading') : t('order.payNow')}
                    </Button>
                  </div>

                  {/* Payment Methods */}
                  <div className="pt-4 text-center">
                    <p className="text-xs text-muted-foreground mb-2">{t('orderTracking.paymentSecure')}</p>
                    <div className="flex justify-center gap-2">
                      <div className="px-3 py-1 bg-gray-100 rounded text-xs font-medium">Visa</div>
                      <div className="px-3 py-1 bg-gray-100 rounded text-xs font-medium">Mastercard</div>
                      <div className="px-3 py-1 bg-gray-100 rounded text-xs font-medium">Bizum</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OrderPage;
